import classNames from 'classnames';
import { Children, PropsWithChildren } from 'react';
import Gravatar from 'react-gravatar';
export type Size = '5xl' | '4xl' | '3xl' | '2xl' | 'xl' | 'l' | 'm' | 's';
export type Variant = 'image' | 'name' | 'emoji';
export type Rounded = 'circle' | 'square' | 'soft';
export type Status = 'online' | 'offline' | 'away' | 'do-not-disturb';
interface EmployeeAvatarProps {
  size: Size;
  src?: string;
  variant?: Variant;
  rounded?: Rounded;
  status?: Status;
  placeholder?: boolean;
  thumbnail?: boolean;
  imageClassName?: string;
  className?: string;
}
interface EmployeeAvatarGroupProps {
  className?: string;
  total?: number;
  size: Size;
}

const EmployeeAvatar = ({
  size,
  src,
  variant = 'image',
  rounded = 'circle',
  status,
  className,
  imageClassName,
  thumbnail,
  children
}: PropsWithChildren<EmployeeAvatarProps>) => {
  // Check if src is a URL (for custom profile image) or an email (for Gravatar)
  const isImageUrl =
    src &&
    (src.startsWith('http') ||
      src.startsWith('/') ||
      src.includes('data:image'));

  return (
    <div
      className={classNames(className, `avatar avatar-${size}`, {
        [`status-${status}`]: status
      })}
    >
      {variant === 'image' &&
        (isImageUrl ? (
          <img
            src={src}
            alt="avatar"
            className={classNames(imageClassName, {
              'img-thumbnail bg-white': thumbnail,
              'avatar-placeholder': !src,
              'rounded-circle': rounded === 'circle',
              'rounded-soft': rounded === 'soft'
            })}
          />
        ) : (
          <Gravatar
            email={src}
            alt="avatar"
            className={classNames(imageClassName, {
              'img-thumbnail bg-white': thumbnail,
              'avatar-placeholder': !src,
              'rounded-circle': rounded === 'circle',
              'rounded-soft': rounded === 'soft'
            })}
          />
        ))}

      {variant === 'name' && (
        <div
          className={classNames('avatar-name', {
            'rounded-circle': rounded === 'circle',
            'rounded-soft': rounded === 'soft'
          })}
        >
          <span>{children}</span>
        </div>
      )}
      {variant === 'emoji' && (
        <div
          className={classNames('avatar-emoji', {
            'rounded-circle': rounded === 'circle',
            'rounded-soft': rounded === 'soft'
          })}
        >
          <span role="img" aria-label="Emoji">
            {children}
          </span>
        </div>
      )}
    </div>
  );
};

export const EmployeeGravatarGroup = ({
  children,
  className,
  total,
  size
}: PropsWithChildren<EmployeeAvatarGroupProps>) => {
  return (
    <div className={classNames(className, 'avatar-group')}>
      {children}
      {total && total > Children.count(children) && (
        <EmployeeAvatar size={size} variant="name">
          +{total - Children.count(children)}
        </EmployeeAvatar>
      )}
    </div>
  );
};

EmployeeAvatar.Group = EmployeeGravatarGroup;

export default EmployeeAvatar;
